import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Category } from "@/types/models";

function ActiveBadge({ active }: { active: boolean }) {
  return (
    <span
      className={cn(
        "inline-block px-2 py-0.5 text-xs uppercase tracking-widest border",
        active
          ? "border-green-600 text-green-700 dark:text-green-400"
          : "border-primary-300 dark:border-primary-700 text-primary-600 dark:text-primary-400"
      )}
    >
      {active ? "Ativa" : "Inativa"}
    </span>
  );
}

export interface CategoryListProps {
  categories: Category[];
  selectedId?: string | null;
  deleting?: boolean;
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
}

function CategoryActions({
  category,
  deleting,
  onEdit,
  onDelete,
}: {
  category: Category;
  deleting: boolean;
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
}) {
  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => onEdit(category)}>
        Editar
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={deleting}
        onClick={() => onDelete(category)}
      >
        Excluir
      </Button>
    </>
  );
}

export function CategoryList({
  categories,
  selectedId,
  deleting = false,
  onEdit,
  onDelete,
}: CategoryListProps) {
  if (categories.length === 0) {
    return (
      <section className="border border-primary-200 dark:border-primary-800 panel-padding">
        <p className="text-sm font-light text-primary-600 dark:text-primary-400">
          Nenhuma categoria cadastrada. Crie a primeira categoria ao lado.
        </p>
      </section>
    );
  }

  return (
    <section className="border border-primary-200 dark:border-primary-800 overflow-hidden">
      <ul className="md:hidden divide-y divide-primary-200 dark:divide-primary-800">
        {categories.map((category) => (
          <li
            key={category.id}
            className={cn(
              "panel-padding space-y-3",
              selectedId === category.id && "bg-primary-50 dark:bg-primary-900/50"
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-base font-light text-primary-900 dark:text-white">
                  {category.name}
                </p>
                <p className="mt-1 text-sm font-light text-primary-600 dark:text-primary-400">
                  {category.slug}
                </p>
              </div>
              <ActiveBadge active={category.active} />
            </div>
            <div className="flex flex-wrap gap-2">
              <CategoryActions
                category={category}
                deleting={deleting}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            </div>
          </li>
        ))}
      </ul>

      <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[36rem] text-sm font-light">
          <thead>
            <tr className="border-b border-primary-200 dark:border-primary-800 text-left text-xs uppercase tracking-widest text-primary-500">
              <th className="px-4 py-4 lg:px-6">Nome</th>
              <th className="px-4 py-4 lg:px-6">Slug</th>
              <th className="px-4 py-4 lg:px-6">Status</th>
              <th className="px-4 py-4 lg:px-6 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((category) => (
              <tr
                key={category.id}
                className={cn(
                  "border-b border-primary-100 dark:border-primary-800 last:border-0",
                  selectedId === category.id && "bg-primary-50 dark:bg-primary-900/50"
                )}
              >
                <td className="px-4 py-4 lg:px-6 text-primary-900 dark:text-white">
                  {category.name}
                </td>
                <td className="px-4 py-4 lg:px-6 text-primary-600 dark:text-primary-400">
                  {category.slug}
                </td>
                <td className="px-4 py-4 lg:px-6">
                  <ActiveBadge active={category.active} />
                </td>
                <td className="px-4 py-4 lg:px-6 text-right space-x-2">
                  <CategoryActions
                    category={category}
                    deleting={deleting}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
